import { BaseService } from "./BaseService";
import { Function, FunctionLog, ErrorResponse, BuildxConfig } from "../types/index";

/**
 * Functions service for Buildx
 * Handles serverless functions management
 * 
 * @example
 * ```typescript
 * const functions = buildx.functions();
 * 
 * // Get all functions
 * const funcs = await functions.list();
 * 
 * // Get function by name
 * const func = await functions.getByName('function-name');
 * 
 * // Update function
 * await functions.update('function-name', { code: 'new code' });
 * ```
 */
export class Functions {
	private baseService: BaseService;

	constructor(config: BuildxConfig) {
		this.baseService = new BaseService(config);
	}

	updateConfig(config: BuildxConfig): void {
		this.baseService.updateConfig(config);
	}

	/**
	 * List all functions in a project
	 */
	async list(projectId?: string): Promise<Function[] | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, "/functions");
		return await this.baseService.get(url);
	}

	/**
	 * Get function by name
	 */
	async getByName(functionName: string, projectId?: string): Promise<Function | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, `/functions/${functionName}`);
		return await this.baseService.get(url);
	}

	/**
	 * Update function
	 */
	async update(functionName: string, data: Partial<Function>, projectId?: string): Promise<Function | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, `/functions/${functionName}`);
		return await this.baseService.post(url, data);
	}

	/**
	 * Get function logs
	 */
	async getLogs(functionName: string, projectId?: string): Promise<FunctionLog[] | ErrorResponse> {
		const url = this.baseService.buildProjectUrl(projectId, `/functions/${functionName}/logs`);
		return await this.baseService.get(url);
	}
} 